import { useNotify, useSchemaConfig } from '@/hooks';
import { useStore } from '@/store';
import {
  usePlugins,
  usePluginLogs,
  useGlobalPluginStore,
  LogType,
} from '@morph-mapper/plugins';
import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { SchemaVariant } from '@morph-mapper/types';
import { recomputeDependencies } from '@/utils';
import { ENVIRONMENT } from '@/config';
import { read, utils } from 'xlsx';
import { renderTemplate, parseFile } from '@/api';
import type { PreConfiguration } from '@/types';
import {
  deserializeSchemaConfig,
  RuntimeSchemaConfig,
} from '@morph-mapper/schema-serialization';

export const usePluginView = () => {
  /* Global State */
  const [
    textContent,
    file,
    files,
    entries,
    setEntries,
    variant,
    preconfig,
    updatePreConfig,
    configSnapshot,
    setValue,
    templateId,
  ] = useStore(({ config: c, entries: e }) => [
    c.getTextContent(),
    c.getFile(),
    c.getFiles(),
    e.entries,
    e.setEntries,
    c.getVariant(),
    c.getPreconfiguration(),
    c.updatePreconfiguration,
    c.getConfigSnapshot(),
    e.setTemplateValue,
    c.getId(),
  ]);

  /* Hooks */
  const { hasConfiguration } = useSchemaConfig();
  const { close, loading, success } = useNotify();
  const schemaConfig = useMemo(() => {
    return deserializeSchemaConfig(configSnapshot);
  }, [configSnapshot]);
  const navigate = useNavigate();
  const { bootstrap, preRender } = usePlugins();
  const [disableNext, setDisableNext] = useState(true);
  const logs = usePluginLogs();
  const [displayLogs, setDisplayLogs] = useState<typeof logs>([]);

  const timestampRef = useRef(0);
  const runOnlyOnce = useRef(false);

  const progressSetup = useCallback(() => {
    // Check if the template uses a configuration,
    // which the user should define before working on the template.
    if (hasConfiguration()) {
      navigate(`/${templateId}/configure`);
    } else {
      navigate(`/${templateId}/entries`);
    }
  }, [hasConfiguration, navigate, templateId]);

  const setDefaultFromMarker = useCallback(
    async (
      variant: SchemaVariant,
      textContent: string,
      preconfig: PreConfiguration,
      updatePreConfig: (
        key: string,
        value: { key: string; options: Record<string, any> },
      ) => void,
      setValue: (id: string, value: any) => void,
      schemaConfig: RuntimeSchemaConfig | undefined,
    ) => {
      try {
        // Find the config ID for _xlsIterator
        const xlsIteratorConfigId = Object.keys(preconfig).find(
          (id) => preconfig[id]?.key === '_xlsIterator',
        );

        if (!xlsIteratorConfigId) {
          return;
        }

        // Check if from marker already has a value (from saved template)
        // If it does, don't overwrite it
        const currentFromValue = preconfig[xlsIteratorConfigId]?.options?.from;
        if (
          currentFromValue !== undefined &&
          currentFromValue !== null &&
          currentFromValue !== ''
        ) {
          return;
        }

        // Call API to get the first cell value
        const testTemplate = {
          default_from_marker: '#0,0$get',
        };

        try {
          const apiResponse = await renderTemplate(
            variant,
            testTemplate,
            textContent,
            {},
          );

          const defaultFromMarker =
            apiResponse?.output?.[0]?.default_from_marker;

          // Only update if we got a valid value from the API
          if (defaultFromMarker !== undefined && defaultFromMarker !== null) {
            const config = preconfig[xlsIteratorConfigId];
            const newOptions = {
              ...config.options,
              from: defaultFromMarker,
            };

            updatePreConfig(xlsIteratorConfigId, {
              key: config.key,
              options: newOptions,
            });

            if (schemaConfig?._xlsIterator) {
              const mutatedValue = schemaConfig._xlsIterator.mutate(newOptions);
              setValue(xlsIteratorConfigId, mutatedValue);
            }
          }
        } catch (apiError) {
          console.error('APIError setting default from marker:', apiError);
        }
      } catch (error) {
        console.error('Error setting default from marker:', error);
      }
    },
    [],
  );

  // Execute the boostrapping function only once when creating internal state from the schema.
  // This may entail remapping (add, move, remove) certain fields in the internal state.
  useEffect(() => {
    if (!schemaConfig && hasConfiguration()) return;
    if (runOnlyOnce.current) return;
    runOnlyOnce.current = true;

    // Advance all active plugin progress entries to at least `value`.
    // Only moves forward — never regresses a plugin that's already further ahead.
    const advanceProgress = (value: number, message: string) => {
      const store = useGlobalPluginStore.getState();
      for (const plugin of Object.keys(store.progress)) {
        if ((store.progress[plugin]?.value ?? 0) < value) {
          store.setProgress(plugin, value, message);
        }
      }
    };

    const run = async () => {
      try {
        await runPipeline();
      } catch (error) {
        console.error('[PluginView] Pipeline error:', error);
        const msg = error instanceof Error ? error.message : String(error);
        useGlobalPluginStore
          .getState()
          .log('Pipeline', `Extraction failed: ${msg}`, LogType.Error);
        // Bar stays where it froze — honest signal that something failed.
        // Unlock Next so the user isn't completely stuck.
        setDisableNext(false);
      }
    };

    const runPipeline = async () => {
      if (schemaConfig.isErr()) return;
      useGlobalPluginStore.getState().resetProgress();

      const local = JSON.parse(JSON.stringify(entries));
      let textNew = '';
      if (file.name.toLowerCase().endsWith('.xlsx')) {
        const wb = read(await file.arrayBuffer());
        const csv = utils.sheet_to_csv(wb.Sheets[wb.SheetNames[0]], {
          FS: '";"',
        });
        textNew = '"' + csv;
      }
      useGlobalPluginStore.getState().setMultiFileContext(0, files.length);
      const modifiedBootstrap = await bootstrap({
        entries: local,
        textContent: textNew ? textNew : textContent,
        recomputeDependencies,
      });
      // Plugin reported ≤85% — pipeline drives the remaining 15%
      advanceProgress(87, 'Applying results...');

      const modifiedPreRender = await preRender({
        entries: local,
        textContent: textNew ? textNew : textContent,
        recomputeDependencies,
        file,
      });
      advanceProgress(92, 'Pre-rendering template...');

      // Set default from marker for CSV/XLS files
      const finalTextContent = textNew ? textNew : textContent;
      const isTableFile =
        variant === SchemaVariant.Table ||
        file.name.toLowerCase().endsWith('.csv') ||
        file.name.toLowerCase().endsWith('.xlsx') ||
        file.name.toLowerCase().endsWith('.xls');

      if (isTableFile && finalTextContent) {
        await setDefaultFromMarker(
          variant,
          finalTextContent,
          preconfig,
          updatePreConfig,
          setValue,
          schemaConfig.value,
        );
        advanceProgress(96, 'Setting up defaults...');
      }

      const remainingFiles = files.slice(1);
      const multiFileNotificationId =
        files.length > 1
          ? loading(`Generalizing template across ${files.length} files...`)
          : null;
      try {
        for (const extraFile of remainingFiles) {
          const extraText = await parseFile(variant, extraFile);
          useGlobalPluginStore
            .getState()
            .setMultiFileContext(
              remainingFiles.indexOf(extraFile) + 1,
              files.length,
            );
          const fileProgress = Math.round(
            96 +
              ((remainingFiles.indexOf(extraFile) + 1) /
                remainingFiles.length) *
                3,
          );
          advanceProgress(
            fileProgress,
            `Generalizing across files: ${remainingFiles.indexOf(extraFile) + 2}/${files.length}`,
          );
          await bootstrap({
            entries: local,
            textContent: extraText,
            recomputeDependencies,
            force: true,
          });
        }
      } catch (loopError) {
        if (multiFileNotificationId) {
          close(multiFileNotificationId);
        }
        throw loopError;
      }

      if (modifiedBootstrap || modifiedPreRender) {
        setEntries(local);
        // Brief pause for React to flush the entries update before navigating
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        progressSetup();
      }
      if (multiFileNotificationId) {
        close(multiFileNotificationId);
        success(`Template generalized across ${files.length} files`);
      }
      // Drive to 100% and wait for the RAF animation to visually catch up before navigating
      advanceProgress(100, 'Done');
      await new Promise((resolve) => setTimeout(resolve, 700));
      if (ENVIRONMENT !== 'DEV') {
        progressSetup();
      }
      setDisableNext(false);
    }; // end runPipeline

    run();
  }, [
    bootstrap,
    close,
    entries,
    file,
    loading,
    preRender,
    preconfig,
    progressSetup,
    schemaConfig,
    setEntries,
    setValue,
    success,
    textContent,
    updatePreConfig,
    variant,
    hasConfiguration,
  ]);

  useEffect(() => {
    if (logs.length === 0) return;

    const ordered = [...logs].sort((a, b) => a.time - b.time);
    const newLogs = ordered.filter((log) => log.time > timestampRef.current);

    if (newLogs.length > 0) {
      setDisplayLogs((prev) => [...prev, ...newLogs]);
      timestampRef.current = newLogs[newLogs.length - 1].time;
    }
  }, [logs]);

  return {
    progressSetup,
    disableNext,
    displayLogs,
  };
};
